import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Layers,
    Wrench,
    ShieldCheck,
    Cpu,
    Award,
    Network,
} from "lucide-react";
import { Button } from "../../components/ui/button";

/**
 * Application Development & Management
 * TechSPR-style enterprise layout
 */
const ApplicationDevelopmentLayout = ({ service }) => {
    return (
        <div className="bg-white min-h-screen">

            {/* ================= HERO ================= */}
            <section
                className="relative bg-cover bg-center text-white py-32"
                style={{
                    backgroundImage: "url('/images/applicationdevelopment/hero.jpg')",
                }}
            >
                {/* Color Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a]/80 via-[#1e293b]/70 to-[#020617]/80" />

                {/* Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-6">
                    <h1 className="text-5xl md:text-6xl font-bold mb-4">
                        Application Development <br /> & Management
                    </h1>

                    <p className="text-xl text-slate-200 max-w-3xl">
                        Improve IT agility, accelerate modernization, and enhance customer
                        experience through scalable application engineering.
                    </p>

                    <div className="mt-8">
                        <Button
                            asChild
                            size="lg"
                            className="bg-orange-500 hover:bg-orange-600 text-white px-8 rounded-xl"
                        >
                            <Link to="/contact" className="flex items-center gap-2">
                                Consult Our Experts <ArrowRight />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>


            {/* ================= INTRO ================= */}
            <section className="py-20 max-w-7xl mx-auto px-6">
                <p className="text-lg text-gray-700 max-w-5xl leading-relaxed">
                    Organizations must continuously modernize and scale applications to
                    stay competitive. We help enterprises design, build, modernize, and
                    manage mission-critical applications using cloud-native, API-first,
                    and DevOps-driven approaches.
                </p>
            </section>

            {/* ================= PLATFORM DEVELOPMENT ================= */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-4xl font-bold mb-6">
                            Application & Platform Development
                        </h2>
                        <p className="text-gray-700 mb-6">
                            Build robust digital platforms that scale with your business.
                            Our teams combine agile engineering, domain expertise, and modern
                            architectures to accelerate delivery.
                        </p>

                        <div className="flex gap-4 flex-wrap">
                            <span className="px-4 py-2 bg-indigo-100 rounded-full text-sm">
                                Agile Development
                            </span>
                            <span className="px-4 py-2 bg-indigo-100 rounded-full text-sm">
                                Cloud-Native
                            </span>
                            <span className="px-4 py-2 bg-indigo-100 rounded-full text-sm">
                                API-First
                            </span>
                            <span className="px-4 py-2 bg-indigo-100 rounded-full text-sm">
                                DevOps
                            </span>
                        </div>

                        <div className="mt-8">
                            <Button
                                asChild
                                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                            >
                                <Link to="/contact">Learn more about our service offerings</Link>
                            </Button>
                        </div>
                    </div>

                    <img
                        src="/images/applicationdevelopment/apd1.png"
                        alt="Application Platform Development"
                        className="rounded-2xl shadow-lg"
                    />
                </div>
            </section>

            {/* ================= CLIENT SUCCESS ================= */}
            <section className="py-24 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-4xl font-bold mb-12">Client Success</h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                img: "/images/applicationdevelopment/client1.jpg",
                                title: "Global Media Company",
                                text: "Reduced technical debt and improved development velocity.",
                            },
                            {
                                img: "/images/applicationdevelopment/client2.jpg",
                                title: "Insurance Enterprise",
                                text: "Optimized claims administration with modern platforms.",
                            },
                            {
                                img: "/images/applicationdevelopment/client3.jpg",
                                title: "FinTech Scale-up",
                                text: "Achieved 50% faster time-to-market for new features.",
                            },
                        ].map((item, i) => (
                            <div key={i} className="bg-slate-800 rounded-2xl overflow-hidden">
                                <img src={item.img} alt={item.title} />
                                <div className="p-6">
                                    <h3 className="font-semibold mb-2">{item.title}</h3>
                                    <p className="text-slate-300 text-sm">{item.text}</p>
                                    <Button
                                        variant="link"
                                        className="text-orange-400 mt-4 px-0"
                                    >
                                        Read the story →
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= WHY US ================= */}
            <section className="py-24 bg-orange-50">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-4xl font-bold mb-12">Why Choose Us?</h2>

                    <div className="grid md:grid-cols-3 gap-10">
                        <Feature
                            icon={<Layers />}
                            title="Deep Application Expertise"
                            text="Decades of experience building large-scale enterprise applications."
                        />
                        <Feature
                            icon={<Award />}
                            title="IP & Accelerators"
                            text="Reusable frameworks that reduce time-to-market."
                        />
                        <Feature
                            icon={<Network />}
                            title="Strong Partner Ecosystem"
                            text="Cloud, DevOps, and platform partnerships."
                        />
                    </div>
                </div>
            </section>

            {/* ================= INSIGHTS ================= */}
            <section className="py-24 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-4xl font-bold mb-12">News & Insights</h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                img: "/images/applicationdevelopment/news1.jpg",
                                title: "Improve software delivery with modern DevOps",
                            },
                            {
                                img: "/images/applicationdevelopment/news3.png",
                                title: "Why legacy modernization matters",
                            },
                            {
                                img: "/images/applicationdevelopment/news2.jpg",
                                title: "Building resilient digital platforms",
                            },
                        ].map((item, i) => (
                            <div key={i} className="bg-slate-800 rounded-2xl overflow-hidden">
                                <img src={item.img} alt={item.title} />
                                <div className="p-6">
                                    <h3 className="font-semibold mb-3">{item.title}</h3>
                                    <Button
                                        variant="link"
                                        className="text-orange-400 px-0"
                                    >
                                        Read more →
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CONTACT ================= */}
            <section className="py-24 bg-gray-100">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-4xl font-bold mb-6">Contact Us</h2>
                    <p className="text-gray-700 max-w-3xl mb-8">
                        Talk to our experts about modernizing your application portfolio.
                    </p>

                    <Button
                        asChild
                        size="lg"
                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                    >
                        <Link to="/contact">Get in Touch</Link>
                    </Button>
                </div>
            </section>

        </div>
    );
};

const Feature = ({ icon, title, text }) => (
    <div className="bg-white p-8 rounded-2xl shadow">
        <div className="w-12 h-12 bg-orange-100 text-orange-600 flex items-center justify-center rounded-xl mb-4">
            {icon}
        </div>
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        <p className="text-gray-600">{text}</p>
    </div>
);

export default ApplicationDevelopmentLayout;
